<template>
  <div>
    <div
      v-if="carregando"
      class="text-center py-4"
    >
      <b-spinner
        small
        variant="primary"
      />
    </div>

    <template v-else>
      <div class="form-row">
        <div class="form-group col-md-6">
          <label>
            <span class="required">*</span> {{ $t('team.form.nameLabel') }}
          </label>
          <input
            v-model="team.name"
            type="text"
            class="form-control"
            maxlength="180"
            autocomplete="off"
          >
        </div>
        <div class="form-group col-md-6">
          <label>{{ $t('team.form.identifierCodeLabel') }}</label>
          <input
            v-model="team.frotas_identifier_code"
            type="text"
            class="form-control"
            maxlength="30"
            placeholder="Ex: SAMU-BNU-01"
          >
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-6">
          <label>{{ $t('team.form.typeLabel') }}</label>
          <v-select
            v-model="team.type_id"
            :options="teamTypes"
            :reduce="t => t.id"
            label="name"
          />
          <small class="text-muted">{{ $t('team.form.typeHint') }}</small>
        </div>
        <div class="form-group col-md-6">
          <label>{{ $t('team.form.disciplineLabel') }}</label>
          <v-select
            v-model="team.discipline_id"
            :options="disciplinas"
            :reduce="d => d.id"
            label="name"
          />
          <small class="text-muted">{{ $t('team.form.disciplineHint') }}</small>
        </div>
      </div>

      <!-- Só na criação: a partir daí a posição muda pelo fluxo operacional
           (embarque/despacho), não pelo cadastro. -->
      <div
        v-if="!team.id"
        class="form-row"
      >
        <div class="form-group col-md-6">
          <label>
            <span
              v-if="posicoesIniciais.length"
              class="required"
            >*</span>
            {{ $t('team.form.initialPositionLabel') }}
          </label>
          <v-select
            v-model="initialPositionTypeId"
            :options="posicoesIniciais"
            :reduce="p => p.id"
            :get-option-label="p => (p.code ? p.code + ' — ' : '') + p.name"
            :disabled="!team.discipline_id || !posicoesIniciais.length"
            :placeholder="placeholderPosicaoInicial"
          />
          <small class="text-muted">{{ dicaPosicaoInicial }}</small>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-3">
          <label>
            <span class="required">*</span> {{ $t('common.status') }}
          </label>
          <select
            v-model.number="team.status"
            class="form-control"
          >
            <option :value="1">
              {{ $t('common.active') }}
            </option>
            <option :value="0">
              {{ $t('common.inactive') }}
            </option>
          </select>
        </div>
      </div>

      <!-- Compartilhamento: outros nós do mesmo tenant passam a enxergar e
           usar a equipe nas telas operacionais. -->
      <div class="form-row">
        <div class="form-group col-md-12">
          <b-form-checkbox
            v-model="team.shares_resource"
            switch
          >
            {{ $t('team.form.sharesResourceLabel') }}
          </b-form-checkbox>
          <small class="text-muted d-block">{{ $t('team.form.sharesResourceHint') }}</small>

          <div
            v-if="team.shares_resource"
            class="mt-1"
          >
            <label>{{ $t('team.form.sharedNodesLabel') }}</label>
            <v-select
              v-model="team.shared_node_ids"
              :options="nosCompartilhaveis"
              :reduce="n => n.id"
              label="name"
              multiple
              :placeholder="$t('team.form.sharedNodesPlaceholder')"
            />
          </div>
        </div>
      </div>

      <!-- Equipamentos: só na edição, a equipe precisa existir para alocar. -->
      <template v-if="team.id">
        <hr>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <h6 class="mb-0">
            {{ $t('team.form.equipmentSectionTitle') }}
          </h6>
          <b-button
            v-if="!mostrarAlocacao"
            size="sm"
            variant="outline-primary"
            @click="abrirAlocacao"
          >
            <feather-icon
              icon="PlusIcon"
              size="13"
              class="mr-25"
            />
            {{ $t('team.form.addEquipmentButton') }}
          </b-button>
        </div>

        <div
          v-if="mostrarAlocacao"
          class="d-flex align-items-end flex-wrap mb-1 p-1 border rounded"
          style="gap: 8px"
        >
          <div style="flex: 1; min-width: 220px">
            <label
              class="mb-25"
              style="font-size: 12px"
            >{{ $t('team.form.equipmentColumn') }}</label>
            <v-select
              v-model="novaAlocacao.equipment_id"
              :options="disponiveis"
              :reduce="e => e.id"
              label="rotulo"
              :placeholder="$t('team.modal.equipmentPlaceholder')"
            />
          </div>
          <div style="width: 100px">
            <label
              class="mb-25"
              style="font-size: 12px"
            >{{ $t('team.form.quantityColumn') }}</label>
            <input
              v-model.number="novaAlocacao.quantity"
              type="number"
              min="1"
              :max="maxDisponivel"
              class="form-control"
            >
          </div>
          <b-button
            size="sm"
            variant="outline-secondary"
            :disabled="alocando"
            @click="mostrarAlocacao = false"
          >
            {{ $t('common.cancel') }}
          </b-button>
          <b-button
            size="sm"
            variant="primary"
            :disabled="alocando || !novaAlocacao.equipment_id"
            @click="alocar"
          >
            {{ alocando ? $t('team.modal.allocatingLabel') : $t('team.modal.allocateButton') }}
          </b-button>
        </div>

        <p
          v-if="equipamentos.length === 0"
          class="text-muted mb-0"
          style="font-size: 13px"
        >
          {{ $t('team.form.noEquipmentAllocated') }}
        </p>

        <table
          v-else
          class="table table-sm mb-0"
        >
          <thead>
            <tr>
              <th>{{ $t('team.form.equipmentColumn') }}</th>
              <th>{{ $t('team.form.serialColumn') }}</th>
              <th class="text-right">
                {{ $t('team.form.quantityColumn') }}
              </th>
              <th>{{ $t('team.form.sinceColumn') }}</th>
              <th class="text-right">
                {{ $t('common.actions') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="a in equipamentos"
              :key="a.id"
            >
              <td>
                {{ a.equipment ? a.equipment.name : '—' }}
                <small
                  v-if="a.equipment && a.equipment.measure_value"
                  class="text-muted"
                >
                  · {{ a.equipment.measure_value }}
                  {{ a.equipment.type && a.equipment.type.unit ? a.equipment.type.unit.abbreviation : '' }}
                </small>
              </td>
              <td>
                <span
                  v-if="a.equipment && a.equipment.serial"
                  class="badge badge-secondary text-monospace"
                >{{ a.equipment.serial }}</span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </td>
              <td class="text-right">
                {{ a.quantity }}
              </td>
              <td>{{ dataHora(a.allocated_at) }}</td>
              <td
                class="text-right"
                style="white-space: nowrap"
              >
                <div
                  v-if="devolvendoId === a.id"
                  class="d-inline-flex align-items-center"
                  style="gap: 6px"
                >
                  <input
                    v-model.number="devolucaoQtd"
                    type="number"
                    class="form-control form-control-sm text-right"
                    style="width: 72px"
                    min="1"
                    :max="a.quantity"
                  >
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    :disabled="devolvendo"
                    @click="devolvendoId = null"
                  >
                    {{ $t('common.cancel') }}
                  </b-button>
                  <b-button
                    size="sm"
                    variant="danger"
                    :disabled="devolvendo || !devolucaoValida(a)"
                    @click="confirmarDevolucao(a)"
                  >
                    {{ devolvendo ? '...' : $t('team.form.confirmButton') }}
                  </b-button>
                </div>
                <b-button
                  v-else
                  size="sm"
                  variant="outline-danger"
                  :title="$t('team.form.returnButton')"
                  @click="abrirDevolucao(a)"
                >
                  <feather-icon
                    icon="CornerUpLeftIcon"
                    size="13"
                  />
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <div class="text-right mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="$emit('cancel')"
        >
          {{ $t('common.cancel') }}
        </b-button>
        <b-button
          v-if="$hasAbility('equipe.gerenciar')"
          variant="primary"
          :disabled="salvando || !podeSalvar"
          @click="salvar"
        >
          {{ $t('common.save') }}
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import { BButton, BFormCheckbox, BSpinner } from 'bootstrap-vue'
import vSelect from 'vue-select'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

/**
 * Formulário de equipe (dados + equipamentos alocados). Usado no modal da
 * aba "Equipes" da administração do nó e na página de edição avulsa.
 *
 * `nodeId` é o nó administrado; sem ele o backend usa o nó operacional.
 * Tipos, disciplinas e equipamentos vêm do nó DONO da equipe — que, editando
 * pelo nó pai, é um sub-nó e não o nó administrado.
 */
export default {
  name: 'TeamForm',
  components: {
    BButton, BFormCheckbox, BSpinner, vSelect,
  },
  mixins: [ApiErrorMixin],

  props: {
    teamId: {
      type: [Number, String],
      default: null,
    },
    nodeId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      team: this.equipeVazia(),
      teamTypes: [],
      disciplinas: [],
      posicoesIniciais: [],
      nos: [],
      initialPositionTypeId: null,
      equipamentos: [],
      disponiveis: [],
      carregando: false,
      salvando: false,
      alocando: false,
      mostrarAlocacao: false,
      novaAlocacao: { equipment_id: null, quantity: 1 },
      devolvendo: false,
      devolvendoId: null,
      devolucaoQtd: 1,
    }
  },

  computed: {
    paramsNo() {
      return this.nodeId ? { node_id: this.nodeId } : {}
    },

    paramsNoDaEquipe() {
      const no = this.team.node_id || this.nodeId
      return no ? { node_id: no } : {}
    },

    // Com posições disponíveis para a disciplina, a posição inicial é
    // obrigatória: assume a padrão ou o usuário escolhe.
    podeSalvar() {
      if (!this.team.name.trim()) return false
      if (!this.team.id && this.posicoesIniciais.length && !this.initialPositionTypeId) return false
      return true
    },

    placeholderPosicaoInicial() {
      if (!this.team.discipline_id) return this.$t('team.form.initialPositionNeedsDiscipline')
      if (!this.posicoesIniciais.length) return this.$t('team.form.initialPositionNone')
      return this.$t('team.form.initialPositionPlaceholder')
    },

    dicaPosicaoInicial() {
      const padrao = this.posicoesIniciais.find(p => p.is_default_position)
      if (padrao) return this.$t('team.form.initialPositionDefaultHint')
      return this.$t('team.form.initialPositionHint')
    },

    // O nó dono da equipe não entra: ela já é dele.
    nosCompartilhaveis() {
      const dono = this.team.node_id || this.nodeId
        || (this.$store.state.selected_node && this.$store.state.selected_node.id)
      return this.nos.filter(n => Number(n.id) !== Number(dono))
    },

    maxDisponivel() {
      const e = this.disponiveis.find(x => x.id === this.novaAlocacao.equipment_id)
      return e ? e.disponivel : null
    },
  },

  watch: {
    'team.discipline_id': function aoMudarDisciplina() {
      if (!this.team.id) this.carregarPosicoesIniciais()
    },
  },

  mounted() {
    this.iniciar()
  },

  methods: {
    equipeVazia() {
      return {
        id: null,
        node_id: null,
        name: '',
        type_id: null,
        discipline_id: null,
        frotas_identifier_code: null,
        status: 1,
        shares_resource: false,
        shared_node_ids: [],
      }
    },

    async iniciar() {
      this.carregando = true
      try {
        if (this.teamId) {
          const res = await this.$http.get(`${this.$store.state.api}teams/${this.teamId}`, { params: this.paramsNo })
          const t = res.data.data[0]
          this.team = {
            id: t.id,
            node_id: t.node_id,
            name: t.name || '',
            type_id: t.type_id,
            discipline_id: t.discipline_id,
            frotas_identifier_code: t.frotas_identifier_code,
            status: Number(t.status),
            shares_resource: !!t.shares_resource,
            shared_node_ids: t.shared_node_ids || [],
          }
          this.carregarEquipamentos()
        }

        // Só depois da equipe: é ela que diz de qual nó vêm as opções.
        await Promise.all([this.carregarTipos(), this.carregarDisciplinas(), this.carregarNos()])
      } catch (e) {
        this.$message(this.$t('common.error'), this.apiErrorText(e), 'error')
        this.$emit('cancel')
      } finally {
        this.carregando = false
      }
    },

    async carregarTipos() {
      try {
        const res = await this.$http.get(`${this.$store.state.api}team_types`, {
          params: { paginated: false, ...this.paramsNoDaEquipe },
        })
        this.teamTypes = res.data.data || []
      } catch (e) {
        this.teamTypes = []
      }
    },

    async carregarDisciplinas() {
      try {
        const res = await this.$http.get(`${this.$store.state.api}discipline/for-node`, {
          params: this.paramsNoDaEquipe,
        })
        this.disciplinas = res.data.data || []
      } catch (e) {
        this.disciplinas = []
      }
    },

    // O backend já limita aos nós do tenant do usuário (e revalida no save).
    async carregarNos() {
      try {
        const res = await this.$http.get(`${this.$store.state.api}nodes`, { params: { paginated: false } })
        this.nos = res.data.data || []
      } catch (e) {
        this.nos = []
      }
    },

    // Estados de viatura ativos da disciplina, no nó da equipe. A padrão
    // (is_default_position) já vem selecionada.
    async carregarPosicoesIniciais() {
      this.posicoesIniciais = []
      this.initialPositionTypeId = null
      if (!this.team.discipline_id) return

      const disciplina = this.team.discipline_id
      try {
        const res = await this.$http.get(`${this.$store.state.api}team_position_types/for-node`, {
          // shown_in_occurrence=0: todas as posições, também as ocultas da ocorrência.
          params: { discipline_id: disciplina, shown_in_occurrence: 0, ...this.paramsNoDaEquipe },
        })
        if (this.team.discipline_id !== disciplina) return

        this.posicoesIniciais = (res.data.data || [])
          .filter(p => Number(p.status) === 1 && (p.scope || 'ocorrencia') === 'veiculo')
        const padroes = this.posicoesIniciais.filter(p => p.is_default_position)
        this.initialPositionTypeId = padroes.length === 1 ? padroes[0].id : null
      } catch (e) {
        this.posicoesIniciais = []
      }
    },

    async salvar() {
      this.salvando = true
      try {
        const base = `${this.$store.state.api}teams`
        // Só os campos do formulário. Desligar o compartilhamento limpa os
        // nós vinculados — senão a equipe seguiria visível neles.
        const corpo = {
          name: this.team.name.trim(),
          type_id: this.team.type_id || null,
          discipline_id: this.team.discipline_id || null,
          frotas_identifier_code: this.team.frotas_identifier_code || null,
          status: this.team.status,
          shares_resource: !!this.team.shares_resource,
          shared_node_ids: this.team.shares_resource ? this.team.shared_node_ids : [],
          ...this.paramsNo,
        }

        if (this.team.id) {
          await this.$http.put(`${base}/${this.team.id}`, corpo)
        } else {
          // Sempre enviado na criação: null significa "sem posição" — sem o
          // campo, o backend aplicaria a padrão por conta própria.
          await this.$http.post(base, { ...corpo, initial_position_type_id: this.initialPositionTypeId })
        }

        this.$message(this.$t('team.messages.saveSuccessTitle'), this.$t('team.messages.saveSuccessMessage'), 'success')
        this.$emit('saved')
      } catch (e) {
        this.$message(this.$t('common.error'), this.apiErrorText(e), 'error')
      } finally {
        this.salvando = false
      }
    },

    async carregarEquipamentos() {
      try {
        const res = await this.$http.get(`${this.$store.state.api}teams/${this.team.id}/equipment`, {
          params: this.paramsNoDaEquipe,
        })
        this.equipamentos = res.data.data || []
      } catch (e) {
        this.equipamentos = []
      }
    },

    async abrirAlocacao() {
      this.novaAlocacao = { equipment_id: null, quantity: 1 }
      try {
        const res = await this.$http.get(`${this.$store.state.api}equipment`, { params: this.paramsNoDaEquipe })
        this.disponiveis = (res.data.data || [])
          .filter(e => e.disponivel > 0)
          .map(e => ({
            ...e,
            rotulo: `${e.name}${e.serial ? ` · ${e.serial}` : ''} (${e.disponivel} ${this.$t('team.modal.availableSuffix')})`,
          }))
        this.mostrarAlocacao = true
      } catch (e) {
        this.$message(
          this.$t('team.messages.loadEquipmentErrorTitle'),
          this.apiErrorText(e, this.$t('team.messages.loadEquipmentErrorMessage')),
          'error',
        )
      }
    },

    async alocar() {
      this.alocando = true
      try {
        await this.$http.post(`${this.$store.state.api}equipment/${this.novaAlocacao.equipment_id}/allocate`, {
          team_id: this.team.id,
          quantity: this.novaAlocacao.quantity,
          ...this.paramsNoDaEquipe,
        })
        this.mostrarAlocacao = false
        this.carregarEquipamentos()
      } catch (e) {
        this.$message(this.$t('common.error'), this.apiErrorText(e, this.$t('team.messages.allocateError')), 'error')
      } finally {
        this.alocando = false
      }
    },

    abrirDevolucao(a) {
      this.devolvendoId = a.id
      this.devolucaoQtd = a.quantity
    },

    devolucaoValida(a) {
      const q = Number(this.devolucaoQtd)
      return q >= 1 && q <= a.quantity
    },

    async confirmarDevolucao(a) {
      if (!this.devolucaoValida(a)) return

      this.devolvendo = true
      try {
        await this.$http.post(`${this.$store.state.api}equipment/allocations/${a.id}/release`, {
          quantity: Number(this.devolucaoQtd),
          ...this.paramsNoDaEquipe,
        })
        this.devolvendoId = null
        this.carregarEquipamentos()
      } catch (e) {
        this.$message(this.$t('common.error'), this.apiErrorText(e, this.$t('team.messages.returnError')), 'error')
      } finally {
        this.devolvendo = false
      }
    },

    dataHora(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('pt-BR')
    },
  },
}
</script>
